import {
  Button,
  Modal,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalOverlay,
  Text,
} from '@chakra-ui/react';
import React, { useContext, useEffect, useState } from 'react';
import { MarketplaceRole, RoleAccount } from 'src/api/v1-api';
import MarketplaceRoleAccountForm from 'src/components/organisms/marketplace-role-account-edit-form';
import { MarketplaceContext } from 'src/contexts/marketplace';
import { useAuth } from 'src/utils/auth';
import useRequest from 'src/utils/hooks/useRequest';

interface AddUserRoleModalProps {
  isOpen: boolean;
  onClose: () => void;
  rolesToAdd: MarketplaceRole[];
  hasSelectedRole: boolean;
  alwaysSelectRole?: boolean;
  modalTitle?: string;
  submitButtonText?: string;
}

const AddUserRoleModal = (props: AddUserRoleModalProps) => {
  const { isOpen, onClose, rolesToAdd, alwaysSelectRole, modalTitle, submitButtonText } = props;
  const { marketplace } = useContext(MarketplaceContext);
  const { user } = useAuth();
  const { api } = useRequest();
  const [selectedRole, setSelectedRole] = useState<MarketplaceRole | null>(null);
  const [roleAccount, setRoleAccount] = useState<RoleAccount | null>(null);
  const hasMultipleRoles = rolesToAdd.length > 1;

  useEffect(() => {
    // TODO: should only set === 1 once we add the dropdown
    if (!selectedRole && rolesToAdd.length > 0) {
      setSelectedRole(rolesToAdd[0]);
    }
  }, [rolesToAdd]);

  // TODO: add the select role dropdown when needed

  const createNormalRole = async () => {
    if (!marketplace?.id || !selectedRole || !user?.id) return;
    const foundRoleAccount = await api.marketplace.user.roles.add(user.id, {
      marketplace_role: selectedRole.id,
      marketplace_id: marketplace?.id,
    });
    setRoleAccount(foundRoleAccount);
  };

  useEffect(() => {
    const fetchRoleAccount = async () => {
      if (!marketplace?.id || !selectedRole || !user?.id) return;
      const foundRoleAccount = await api.marketplace.user.role.get(user.id, selectedRole.id);
      if (foundRoleAccount?.id) {
        setRoleAccount(foundRoleAccount);
      } else {
        // role account must be created for the user before the payment process.
        // for now, create the payment-required role when they open this modal.
        if (!selectedRole.functional.requires_payment) return;

        const newRoleAccount = await api.marketplace.user.roles.add(user.id, {
          marketplace_role: selectedRole.id,
          marketplace_id: marketplace?.id,
        });
        setRoleAccount(newRoleAccount);
      }
    };

    if (!isOpen || !marketplace?.id || !selectedRole || !user?.id) return;
    fetchRoleAccount();
  }, [isOpen, marketplace?.id, user?.id, selectedRole]);

  const onCloseHandler = () => {
    onClose && onClose();
  };

  return (
    <Modal size="xl" isCentered isOpen={isOpen} onClose={onCloseHandler}>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>{modalTitle ?? 'Add User Role'}</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          {(alwaysSelectRole || hasMultipleRoles) && (
            <>
              {/* add the select role dropdown here */}
              select role
            </>
          )}
          {selectedRole && roleAccount && selectedRole.functional.requires_payment === false && (
            <>
              <Text>
                Role to Add: <b>{selectedRole?.name}</b>
              </Text>
              <Button colorScheme="primary" onClick={() => createNormalRole()}>
                {submitButtonText ?? 'Add User Role'}
              </Button>
            </>
          )}
          {isOpen && selectedRole && roleAccount && (
            <MarketplaceRoleAccountForm roleAccount={roleAccount} />
          )}
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

export default AddUserRoleModal;
